import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import {
  getTcpackages,
  getBoboPackages,
  getPackageProducts,
  getBoboPackageProducts,
  getTelcoBillSettings,
  createTelcoBillSettings,
  updateTelcoBillSettings,
  getTelcoBillSettingsPricing,
  createRegionSettings,
  getRegionSettingsByOrganization,
  getRegionByStateCode,
  updateRegionSettings,
  deleteRegionSettings,
} from './telcobillPackageSettings.action';
import {
  getAtaModels,
  createAtaModel,
  deleteAtaModel,
  getAtaModelsMaxLines as getMaxLines,
  getAvailableAtaModels as getModels,
  getAvailableAtaBrands as getBrands,
} from './ataModels.action';
import {
  TelcoBillSettingsType,
  TelcoBillSettingsResponse,
  PackagesResponse,
  PackageProductsResponse,
  BoboPackagesResponse,
  BoboPackageProductsResponse,
  RegionSettingsResponse,
  StateRegionResponse,
  AtaModelsResponse,
  AtaModelsMaxLinesResponse,
} from '../../types/telcobill-package-settings/telcobillPackageSettings.types';
import { ModelType, BrandWithModelsType } from '../../types/inventory/inventory.types';

export interface TelcoBillPackageSettingsState {
  // TC Packages
  tcPackages: PackagesResponse | null;
  tcPackagesFetchStatus: FETCH_STATUS;

  // Bobo Packages
  boboPackages: BoboPackagesResponse | null;
  boboPackagesFetchStatus: FETCH_STATUS;

  // Package Products
  packageProducts: PackageProductsResponse | null;
  packageProductsFetchStatus: FETCH_STATUS;

  // Bobo Package Products
  boboPackageProducts: BoboPackageProductsResponse | null;
  boboPackageProductsFetchStatus: FETCH_STATUS;

  // TelcoBill Settings
  telcoBillSettings: TelcoBillSettingsResponse | TelcoBillSettingsType[] | null;
  telcoBillSettingsFetchStatus: FETCH_STATUS;
  telcoBillSettingsCreateStatus: FETCH_STATUS;
  telcoBillSettingsUpdateStatus: FETCH_STATUS;

  // Region Settings
  regionSettings: RegionSettingsResponse | null;
  regionSettingsFetchStatus: FETCH_STATUS;
  regionSettingsCreateStatus: FETCH_STATUS;
  regionSettingsUpdateStatus: FETCH_STATUS;
  regionSettingsDeleteStatus: FETCH_STATUS;
  stateRegionLookup: StateRegionResponse | null;
  stateRegionLookupStatus: FETCH_STATUS;

  // ATA Models
  ataModels: AtaModelsResponse | null;
  ataModelsFetchStatus: FETCH_STATUS;
  ataModelsCreateStatus: FETCH_STATUS;
  ataModelsDeleteStatus: FETCH_STATUS;
  ataModelsMaxLines: AtaModelsMaxLinesResponse | null;
  ataModelsMaxLinesFetchStatus: FETCH_STATUS;
  availableAtaModels: ModelType[] | null;
  availableAtaModelsFetchStatus: FETCH_STATUS;
  availableAtaBrands: BrandWithModelsType[] | null;
  availableAtaBrandsFetchStatus: FETCH_STATUS;
}

const initialState: TelcoBillPackageSettingsState = {
  // TC Packages
  tcPackages: null,
  tcPackagesFetchStatus: FETCH_STATUS.NULL,

  // Bobo Packages
  boboPackages: null,
  boboPackagesFetchStatus: FETCH_STATUS.NULL,

  // Package Products
  packageProducts: null,
  packageProductsFetchStatus: FETCH_STATUS.NULL,

  // Bobo Package Products
  boboPackageProducts: null,
  boboPackageProductsFetchStatus: FETCH_STATUS.NULL,

  // TelcoBill Settings
  telcoBillSettings: null,
  telcoBillSettingsFetchStatus: FETCH_STATUS.NULL,
  telcoBillSettingsCreateStatus: FETCH_STATUS.NULL,
  telcoBillSettingsUpdateStatus: FETCH_STATUS.NULL,
  // Region Settings
  regionSettings: null,
  regionSettingsFetchStatus: FETCH_STATUS.NULL,
  regionSettingsCreateStatus: FETCH_STATUS.NULL,
  regionSettingsUpdateStatus: FETCH_STATUS.NULL,
  regionSettingsDeleteStatus: FETCH_STATUS.NULL,
  stateRegionLookup: null,
  stateRegionLookupStatus: FETCH_STATUS.NULL,

  // ATA Models
  ataModels: null,
  ataModelsFetchStatus: FETCH_STATUS.NULL,
  ataModelsCreateStatus: FETCH_STATUS.NULL,
  ataModelsDeleteStatus: FETCH_STATUS.NULL,
  ataModelsMaxLines: null,
  ataModelsMaxLinesFetchStatus: FETCH_STATUS.NULL,
  availableAtaModels: null,
  availableAtaModelsFetchStatus: FETCH_STATUS.NULL,
  availableAtaBrands: null,
  availableAtaBrandsFetchStatus: FETCH_STATUS.NULL,
};

const telcobillPackageSettingsSlice = createSlice({
  name: 'telcobillPackageSettings',
  initialState,
  reducers: {
    // TC Packages
    setTcPackages: (state, action: PayloadAction<PackagesResponse | null>) => {
      state.tcPackages = action.payload;
    },
    setTcPackagesFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.tcPackagesFetchStatus = action.payload;
    },

    // Bobo Packages
    setBoboPackages: (state, action: PayloadAction<BoboPackagesResponse | null>) => {
      state.boboPackages = action.payload;
    },
    setBoboPackagesFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.boboPackagesFetchStatus = action.payload;
    },

    // Package Products
    setPackageProducts: (state, action: PayloadAction<PackageProductsResponse | null>) => {
      state.packageProducts = action.payload;
    },
    setPackageProductsFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.packageProductsFetchStatus = action.payload;
    },

    // Bobo Package Products
    setBoboPackageProducts: (state, action: PayloadAction<BoboPackageProductsResponse | null>) => {
      state.boboPackageProducts = action.payload;
    },
    setBoboPackageProductsFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.boboPackageProductsFetchStatus = action.payload;
    },

    // TelcoBill Settings
    setTelcoBillSettings: (state, action: PayloadAction<TelcoBillSettingsResponse | null>) => {
      state.telcoBillSettings = action.payload;
    },
    setTelcoBillSettingsFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.telcoBillSettingsFetchStatus = action.payload;
    },
    setTelcoBillSettingsCreateStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.telcoBillSettingsCreateStatus = action.payload;
    },
    setTelcoBillSettingsUpdateStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.telcoBillSettingsUpdateStatus = action.payload;
    },

    // Region Settings
    setRegionSettings: (state, action: PayloadAction<RegionSettingsResponse | null>) => {
      state.regionSettings = action.payload;
    },
    setRegionSettingsFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.regionSettingsFetchStatus = action.payload;
    },
    setRegionSettingsCreateStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.regionSettingsCreateStatus = action.payload;
    },
    setRegionSettingsUpdateStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.regionSettingsUpdateStatus = action.payload;
    },
    setRegionSettingsDeleteStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.regionSettingsDeleteStatus = action.payload;
    },
    setStateRegionLookup: (state, action: PayloadAction<StateRegionResponse | null>) => {
      state.stateRegionLookup = action.payload;
    },
    setStateRegionLookupStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.stateRegionLookupStatus = action.payload;
    },

    // ATA Models
    setAtaModels: (state, action: PayloadAction<AtaModelsResponse | null>) => {
      state.ataModels = action.payload;
    },
    setAtaModelsFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.ataModelsFetchStatus = action.payload;
    },
    setAtaModelsCreateStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.ataModelsCreateStatus = action.payload;
    },
    setAtaModelsDeleteStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.ataModelsDeleteStatus = action.payload;
    },
    setAtaModelsMaxLines: (state, action: PayloadAction<AtaModelsMaxLinesResponse | null>) => {
      state.ataModelsMaxLines = action.payload;
    },
    setAtaModelsMaxLinesFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.ataModelsMaxLinesFetchStatus = action.payload;
    },
    setAvailableAtaModels: (state, action: PayloadAction<ModelType[] | null>) => {
      state.availableAtaModels = action.payload;
    },
    setAvailableAtaModelsFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.availableAtaModelsFetchStatus = action.payload;
    },
    setAvailableAtaBrands: (state, action: PayloadAction<BrandWithModelsType[] | null>) => {
      state.availableAtaBrands = action.payload;
    },
    setAvailableAtaBrandsFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.availableAtaBrandsFetchStatus = action.payload;
    },

    resetTelcoBillPackageSettingsState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getTcpackages.pending, state => {
        state.tcPackagesFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getTcpackages.fulfilled, (state, action) => {
        state.tcPackagesFetchStatus = FETCH_STATUS.FULFILLED;
        state.tcPackages = action.payload;
      })
      .addCase(getTcpackages.rejected, (state, action) => {
        state.tcPackagesFetchStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(getBoboPackages.pending, state => {
        state.boboPackagesFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getBoboPackages.fulfilled, (state, action) => {
        state.boboPackagesFetchStatus = FETCH_STATUS.FULFILLED;
        state.boboPackages = action.payload;
      })
      .addCase(getBoboPackages.rejected, (state, action) => {
        state.boboPackagesFetchStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(getPackageProducts.pending, state => {
        state.packageProductsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getPackageProducts.fulfilled, (state, action) => {
        state.packageProductsFetchStatus = FETCH_STATUS.FULFILLED;
        state.packageProducts = action.payload;
      })
      .addCase(getPackageProducts.rejected, (state, action) => {
        state.packageProductsFetchStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(getBoboPackageProducts.pending, state => {
        state.boboPackageProductsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getBoboPackageProducts.fulfilled, (state, action) => {
        state.boboPackageProductsFetchStatus = FETCH_STATUS.FULFILLED;
        state.boboPackageProducts = action.payload;
      })
      .addCase(getBoboPackageProducts.rejected, (state, action) => {
        state.boboPackageProductsFetchStatus = FETCH_STATUS.REJECTED;
      })

      // TelcoBill Settings
      .addCase(getTelcoBillSettings.pending, state => {
        state.telcoBillSettingsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getTelcoBillSettings.fulfilled, (state, action) => {
        state.telcoBillSettingsFetchStatus = FETCH_STATUS.FULFILLED;
        state.telcoBillSettings = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.items || action.payload;
      })
      .addCase(getTelcoBillSettings.rejected, (state, action) => {
        state.telcoBillSettingsFetchStatus = FETCH_STATUS.REJECTED;
      })

      // Create TelcoBill Settings
      .addCase(createTelcoBillSettings.pending, state => {
        state.telcoBillSettingsCreateStatus = FETCH_STATUS.PENDING;
      })
      .addCase(createTelcoBillSettings.fulfilled, (state, action) => {
        state.telcoBillSettingsCreateStatus = FETCH_STATUS.FULFILLED;
        // Handle both array and response format
        if (action.payload) {
          const newItem = action.payload;
          if (Array.isArray(state.telcoBillSettings)) {
            // If it's an array, filter out existing item with same creditType and add new one
            const updatedArray = state.telcoBillSettings.filter(item => item.creditType !== newItem.creditType);
            updatedArray.push(newItem);
            state.telcoBillSettings = updatedArray;
          } else if (state.telcoBillSettings && state.telcoBillSettings.items) {
            // If it's a response format with items
            const existingItems = state.telcoBillSettings.items;
            const updatedItems = existingItems.filter(item => item.creditType !== newItem.creditType);
            updatedItems.push(newItem);
            state.telcoBillSettings = {
              ...state.telcoBillSettings,
              items: updatedItems,
              total: updatedItems.length,
            };
          } else {
            // If no existing data, create new array
            state.telcoBillSettings = [newItem];
          }
        }
      })
      .addCase(createTelcoBillSettings.rejected, (state, action) => {
        state.telcoBillSettingsCreateStatus = FETCH_STATUS.REJECTED;
      })

      // Update TelcoBill Settings
      .addCase(updateTelcoBillSettings.pending, state => {
        state.telcoBillSettingsUpdateStatus = FETCH_STATUS.PENDING;
      })
      .addCase(updateTelcoBillSettings.fulfilled, (state, action) => {
        state.telcoBillSettingsUpdateStatus = FETCH_STATUS.FULFILLED;
        // Handle both array and response format
        if (action.payload) {
          const updatedItem = action.payload;
          if (Array.isArray(state.telcoBillSettings)) {
            // If it's an array, update the specific item
            state.telcoBillSettings = state.telcoBillSettings.map(item =>
              item.creditType === updatedItem.creditType ? updatedItem : item
            );
          } else if (state.telcoBillSettings && state.telcoBillSettings.items) {
            // If it's a response format with items
            const existingItems = state.telcoBillSettings.items;
            const updatedItems = existingItems.map(item =>
              item.creditType === updatedItem.creditType ? updatedItem : item
            );
            state.telcoBillSettings = {
              ...state.telcoBillSettings,
              items: updatedItems,
            };
          }
        }
      })
      .addCase(updateTelcoBillSettings.rejected, (state, action) => {
        state.telcoBillSettingsUpdateStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(getTelcoBillSettingsPricing.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedItem = action.payload;
          if (Array.isArray(state.telcoBillSettings)) {
            state.telcoBillSettings = state.telcoBillSettings.map(item =>
              item.creditType === updatedItem.creditType ? { ...item, revioData: updatedItem.revioData } : item
            );
          } else if (state.telcoBillSettings && state.telcoBillSettings.items) {
            const existingItems = state.telcoBillSettings.items;
            const updatedItems = existingItems.map(item =>
              item.creditType === updatedItem.creditType ? { ...item, revioData: updatedItem.revioData } : item
            );
            state.telcoBillSettings = {
              ...state.telcoBillSettings,
              items: updatedItems,
            };
          }
        }
      })

      // Region Settings
      .addCase(getRegionSettingsByOrganization.pending, state => {
        state.regionSettingsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getRegionSettingsByOrganization.fulfilled, (state, action) => {
        state.regionSettingsFetchStatus = FETCH_STATUS.FULFILLED;
        state.regionSettings = action.payload || {
          items: [],
          total: 0,
          page: 1,
          limit: 10,
        };
      })
      .addCase(getRegionSettingsByOrganization.rejected, state => {
        state.regionSettingsFetchStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(createRegionSettings.pending, state => {
        state.regionSettingsCreateStatus = FETCH_STATUS.PENDING;
      })
      .addCase(createRegionSettings.fulfilled, (state, action) => {
        state.regionSettingsCreateStatus = FETCH_STATUS.FULFILLED;
        if (action.payload) {
          state.regionSettings = {
            items: state.regionSettings?.items ? [...state.regionSettings.items, action.payload] : [action.payload],
            total: (state.regionSettings?.items?.length || 0) + 1,
            page: 1,
            limit: 10,
          };
        }
      })
      .addCase(createRegionSettings.rejected, state => {
        state.regionSettingsCreateStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(updateRegionSettings.pending, state => {
        state.regionSettingsUpdateStatus = FETCH_STATUS.PENDING;
      })
      .addCase(updateRegionSettings.fulfilled, (state, action) => {
        state.regionSettingsUpdateStatus = FETCH_STATUS.FULFILLED;
        if (action.payload) {
          const existingItems = state.regionSettings?.items || [];
          const index = existingItems.findIndex(item => item.id === action.payload.id);

          let updatedItems: any[];
          if (index !== -1) {
            // Update existing item
            updatedItems = existingItems.map((item, i) => (i === index ? action.payload : item));
          } else {
            // Add new item if it doesn't exist
            updatedItems = [...existingItems, action.payload];
          }

          state.regionSettings = {
            items: updatedItems,
            total: updatedItems.length,
            page: 1,
            limit: 10,
          };
        }
      })
      .addCase(updateRegionSettings.rejected, state => {
        state.regionSettingsUpdateStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(deleteRegionSettings.pending, state => {
        state.regionSettingsDeleteStatus = FETCH_STATUS.PENDING;
      })
      .addCase(deleteRegionSettings.fulfilled, (state, action) => {
        state.regionSettingsDeleteStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(deleteRegionSettings.rejected, state => {
        state.regionSettingsDeleteStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(getRegionByStateCode.pending, state => {
        state.stateRegionLookupStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getRegionByStateCode.fulfilled, (state, action) => {
        state.stateRegionLookupStatus = FETCH_STATUS.FULFILLED;
        state.stateRegionLookup = action.payload;
      })
      .addCase(getRegionByStateCode.rejected, state => {
        state.stateRegionLookupStatus = FETCH_STATUS.REJECTED;
      })

      // ATA Models
      .addCase(getAtaModels.pending, state => {
        state.ataModelsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getAtaModels.fulfilled, (state, action) => {
        state.ataModelsFetchStatus = FETCH_STATUS.FULFILLED;
        state.ataModels = action.payload;
      })
      .addCase(getAtaModels.rejected, state => {
        state.ataModelsFetchStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(createAtaModel.pending, state => {
        state.ataModelsCreateStatus = FETCH_STATUS.PENDING;
      })
      .addCase(createAtaModel.fulfilled, (state, action) => {
        state.ataModelsCreateStatus = FETCH_STATUS.FULFILLED;
        if (action.payload && state.ataModels) {
          const currentModels = Array.isArray(state.ataModels) ? state.ataModels : [];
          state.ataModels = [...currentModels, action.payload];
        }
      })
      .addCase(createAtaModel.rejected, state => {
        state.ataModelsCreateStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(deleteAtaModel.pending, state => {
        state.ataModelsDeleteStatus = FETCH_STATUS.PENDING;
      })
      .addCase(deleteAtaModel.fulfilled, (state, action) => {
        state.ataModelsDeleteStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(deleteAtaModel.rejected, state => {
        state.ataModelsDeleteStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(getMaxLines.pending, state => {
        state.ataModelsMaxLinesFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getMaxLines.fulfilled, (state, action) => {
        state.ataModelsMaxLinesFetchStatus = FETCH_STATUS.FULFILLED;
        state.ataModelsMaxLines = action.payload;
      })
      .addCase(getMaxLines.rejected, state => {
        state.ataModelsMaxLinesFetchStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(getModels.pending, state => {
        state.availableAtaModelsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getModels.fulfilled, (state, action) => {
        state.availableAtaModelsFetchStatus = FETCH_STATUS.FULFILLED;
        state.availableAtaModels = action.payload;
      })
      .addCase(getModels.rejected, state => {
        state.availableAtaModelsFetchStatus = FETCH_STATUS.REJECTED;
      })

      .addCase(getBrands.pending, state => {
        state.availableAtaBrandsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getBrands.fulfilled, (state, action) => {
        state.availableAtaBrandsFetchStatus = FETCH_STATUS.FULFILLED;
        state.availableAtaBrands = action.payload;
      })
      .addCase(getBrands.rejected, state => {
        state.availableAtaBrandsFetchStatus = FETCH_STATUS.REJECTED;
      });
  },
});

export const { setPackageProducts, setPackageProductsFetchStatus } = telcobillPackageSettingsSlice.actions;

export default telcobillPackageSettingsSlice.reducer;
